import { useState, useEffect } from 'react';
import TemplateFormSectionModel from '../models/templating/api/TemplateFormSectionModel';
import TemplateFormFieldModel from '../models/templating/api/TemplateFormFieldModel';
import generateGuid from '../services/guids';
import useCSVHandler from './UseCSVHandler';

const useTemplateFormFieldsHandler = () => {
    const [errors, setErrors] = useState<string[]>([]);
    const { importedData, importCSV } = useCSVHandler();

    // Custom CSV export function for templates (without the filtering logic)
    const exportTemplateCSV = (data: any[], headers: string[], filename: string) => {
        // Convert to CSV friendly format
        const convertToCSVFriendly = (text: string) => {
            if (text == null || text === undefined) return "";

            const textStr = String(text);
            if (!textStr.length) return "";

            // Check if text starts with characters that Excel interprets as formulas
            const startsWithFormulaChar = /^[=+\-@]/.test(textStr);

            // Check if we need to wrap in quotes (contains comma, quotes, newlines, or starts with formula characters)
            if (textStr.includes(',') || textStr.includes('"') || textStr.includes('\n') || textStr.includes('\r') || startsWithFormulaChar) {
                // For formula characters, prefix with single quote to prevent Excel interpretation
                const escapedText = startsWithFormulaChar ? `'${textStr}` : textStr;
                // Wrap the entire text in double quotes and escape any existing double quotes
                return `"${escapedText.replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
            }

            // If no special characters, return the text as-is
            return textStr;
        };

        let csvContent = `${headers.map(header => convertToCSVFriendly(header)).join(',')}\n`;

        data.forEach(item => {
            // Use headers to ensure column order matches
            const row = headers.map(header => {
                const value = item[header];
                return convertToCSVFriendly(value != null ? String(value) : '');
            }).join(',');

            csvContent += row + '\n';
        });

        // Add BOM to ensure correct encoding
        const bom = '\uFEFF';
        const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const exportTemplateFormFields = (templateConfiguration: any, templateName?: string) => {
        try {
            setErrors([]); // Clear previous errors

            if (!templateConfiguration) {
                setErrors(['No template configuration available for export. Please refresh the page to start over.']);
                return;
            }

            const exportData = [];

            // Extract all form fields from all sections
            templateConfiguration?.templateForm?.templateFormSections?.forEach(section => {
                section.templateFormFields?.forEach(field => {
                    try {
                        exportData.push({
                            sectionId: null, // Set to null so new IDs are generated on import
                            sectionTitle: section.title,
                            sectionPriority: section.priority,
                            fieldId: null, // Set to null so new IDs are generated on import
                            fieldTitle: field.title,
                            fieldLabel: field.label || '', // Add the label field for "Text Field Label"
                            fieldType: field.fieldType,
                            associatedVariable: field.associatedVariable,
                            placeholder: field.placeholder,
                            tip: field.tip,
                            defaultValue: field.defaultValue,
                            options: field.options ? JSON.stringify(field.options) : '',
                            isRequired: field.isRequired,
                            priority: field.priority
                        });
                    } catch (fieldError) {
                        setErrors(prev => [...prev, `Error processing field "${field.title}": ${fieldError.message}`]);
                    }
                });
            });

            if (exportData.length === 0) {
                setErrors(['No form fields to export. Please add some form fields to your template before exporting.']);
                return;
            }

            const headers = [
                'sectionId', 'sectionTitle', 'sectionPriority', 'fieldId', 'fieldTitle',
                'fieldLabel', 'fieldType', 'associatedVariable', 'placeholder', 'tip', 'defaultValue',
                'options', 'isRequired', 'priority'
            ];

            // Generate filename
            const name = templateName || 'template';
            const date = new Date();
            const formattedDate = `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
            const formattedTime = `${date.getHours()}-${date.getMinutes()}`;
            const filename = `${name.toLowerCase().replace(/\s+/g, '-')}-form-fields-${formattedDate}-${formattedTime}`;

            exportTemplateCSV(exportData, headers, filename);

        } catch (error) {
            setErrors([`Export failed: ${error.message}. Please try again or refresh the page to start over.`]);
        }
    };

    const convertCSVToTemplateSections = (csvArray: string[][]) => {
        try {
            setErrors([]); // Clear previous errors

            if (!csvArray || csvArray.length === 0) {
                const error = 'CSV file is empty or invalid. Please select a valid CSV file with template data, or refresh the page to start over.';
                setErrors([error]);
                return [];
            }

            if (csvArray.length < 2) {
                const error = 'CSV file must contain headers and at least one data row. Please check your file format or refresh the page to start over.';
                setErrors([error]);
                return [];
            }

            const csvRows = csvArray.slice(1); // Skip header row
            const sectionsMap = new Map<string, any>();
            let importErrors: string[] = [];

            // Validate headers
            const headers = csvArray[0];
            const expectedHeaders = ['sectionId', 'sectionTitle', 'sectionPriority', 'fieldId', 'fieldTitle',
                'fieldLabel', 'fieldType', 'associatedVariable', 'placeholder', 'tip', 'defaultValue',
                'options', 'isRequired', 'priority'];

            if (headers.length < expectedHeaders.length) {
                const missingHeaders = expectedHeaders.slice(headers.length);
                importErrors.push(`CSV file is missing required columns: ${missingHeaders.join(', ')}. Expected ${expectedHeaders.length} columns, found ${headers.length}. Please update your CSV file with all required columns or refresh the page to start over.`);
            }

            csvRows.forEach((row, rowIndex) => {
                try {
                    // Skip empty rows
                    if (row.length === 0 || row.every(cell => !cell || cell.trim() === '')) {
                        return;
                    }

                    if (row.length < 14) {
                        const missingColumns = expectedHeaders.slice(row.length);
                        importErrors.push(`Row ${rowIndex + 2} is missing required columns: ${missingColumns.join(', ')}. Please complete all required field values before saving or refresh the page to start over.`);
                        return;
                    }

                    const [sectionId, sectionTitle, sectionPriority, fieldId, fieldTitle,
                        fieldLabel, fieldType, associatedVariable, placeholder, tip, defaultValue,
                        options, isRequired, priority] = row;

                    // Validate required fields
                    if (!sectionTitle || sectionTitle.trim() === '') {
                        importErrors.push(`Row ${rowIndex + 2}: Missing value for "sectionTitle" column. Please complete this required field before saving or refresh the page to start over.`);
                        return;
                    }

                    if (!fieldTitle || fieldTitle.trim() === '') {
                        importErrors.push(`Row ${rowIndex + 2}: Missing value for "fieldTitle" column. Please complete this required field before saving or refresh the page to start over.`);
                        return;
                    }

                    if (!fieldType || fieldType.trim() === '') {
                        importErrors.push(`Row ${rowIndex + 2}: Missing value for "fieldType" column. Please specify a valid field type (text, textarea, select, etc.) before saving or refresh the page to start over.`);
                        return;
                    }

                    // Group fields by section title
                    if (!sectionsMap.has(sectionTitle)) {
                        sectionsMap.set(sectionTitle, {
                            title: sectionTitle,
                            priority: parseInt(sectionPriority) || 0,
                            fields: []
                        });
                    }

                    const section = sectionsMap.get(sectionTitle);
                    try {
                        let parsedOptions = [];
                        if (options && options.trim() !== '') {
                            try {
                                parsedOptions = JSON.parse(options);
                                if (!Array.isArray(parsedOptions)) {
                                    parsedOptions = [];
                                }
                            } catch (optionsError) {
                                importErrors.push(`Row ${rowIndex + 2}: Invalid JSON format in "options" column. Expected format: ["Option 1", "Option 2"]. Please fix this field before saving or refresh the page to start over.`);
                                parsedOptions = [];
                            }
                        }

                        section.fields.push({
                            title: fieldTitle,
                            label: fieldLabel,
                            fieldType: fieldType,
                            associatedVariable: associatedVariable || '',
                            placeholder: placeholder || '',
                            tip: tip || '',
                            defaultValue: defaultValue || '',
                            options: parsedOptions,
                            isRequired: isRequired === 'true' || isRequired === 'TRUE',
                            priority: parseInt(priority) || 0
                        });
                    } catch (fieldError) {
                        importErrors.push(`Row ${rowIndex + 2}: Error processing field "${fieldTitle || 'unknown'}": ${fieldError.message}. Please check the field data or refresh the page to start over.`);
                    }
                } catch (rowError) {
                    importErrors.push(`Row ${rowIndex + 2}: Error processing data - please check that all required columns have valid values: ${rowError.message}. Complete all required fields before saving or refresh the page to start over.`);
                }
            });

            setErrors(importErrors);

            const sectionsArray = Array.from(sectionsMap.values());

            if (sectionsArray.length === 0 && importErrors.length === 0) {
                const error = 'No valid sections found in CSV file. Please check your data format and ensure all required fields are completed, or refresh the page to start over.';
                setErrors([error]);
            }

            return sectionsArray;
        } catch (error) {
            setErrors([`Failed to process CSV file: ${error.message}. Please check your file format and try again, or refresh the page to start over.`]);
            return [];
        }
    };

    const importTemplateFormFields = (file: File) => {
        setErrors([]); // Clear previous errors

        try {
            importCSV(file, convertCSVToTemplateSections);
        } catch (error) {
            setErrors([`Failed to start import process: ${error.message}. Please try again or refresh the page to start over.`]);
        }
    };

    const createTemplateSections = (importedSections: any[], existingTemplate: any) => {
        try {
            const templateConfig = { ...existingTemplate };

            if (!templateConfig.templateForm) {
                templateConfig.templateForm = {
                    templateFormSections: [],
                    instructionsMarkdown: ''
                };
            }

            // Clear existing sections - replace with imported ones
            templateConfig.templateForm.templateFormSections = [];

            let creationErrors: string[] = [];

            // Add imported sections and fields
            importedSections.forEach((importedSection, sectionIndex) => {
                try {
                    if (!importedSection.title) {
                        creationErrors.push(`Section ${sectionIndex + 1}: Missing section title. Please ensure the "sectionTitle" column has a value for this section before saving.`);
                        return;
                    }

                    const newSection: TemplateFormSectionModel = {
                        id: generateGuid(),
                        title: importedSection.title,
                        priority: importedSection.priority || sectionIndex,
                        instructionsMarkdown: '',
                        templateFormFields: []
                    };

                    if (importedSection.fields && Array.isArray(importedSection.fields)) {
                        newSection.templateFormFields = importedSection.fields.map((field, index) => {
                            try {
                                if (!field.title) {
                                    creationErrors.push(`Field ${index + 1} in section "${importedSection.title}": Missing field title. Please ensure the "fieldTitle" column has a value before saving.`);
                                    return null;
                                }

                                const newField: TemplateFormFieldModel = {
                                    id: generateGuid(),
                                    templateFormSectionId: newSection.id,
                                    title: field.title,
                                    label: field.label,
                                    fieldType: field.fieldType || 'text',
                                    associatedVariable: field.associatedVariable || '',
                                    placeholder: field.placeholder || '',
                                    tip: field.tip || '',
                                    defaultValue: field.defaultValue || '',
                                    options: field.options || [],
                                    isRequired: field.isRequired || false,
                                    priority: field.priority || index
                                };

                                return newField;
                            } catch (fieldError) {
                                creationErrors.push(`Error creating field "${field.title || `#${index + 1}`}" in section "${importedSection.title}": ${fieldError.message}. Please check the field data before saving.`);
                                return null;
                            }
                        }).filter(field => field !== null);
                    }

                    templateConfig.templateForm.templateFormSections.push(newSection);
                } catch (sectionError) {
                    creationErrors.push(`Error processing section "${importedSection.title || `#${sectionIndex + 1}`}": ${sectionError.message}. Please check the section data before saving.`);
                }
            });

            // Re-sort all sections by priority
            templateConfig.templateForm.templateFormSections.sort((a, b) => a.priority - b.priority);
            templateConfig.templateForm.templateFormSections.forEach((s, i) => {
                s.priority = i;
            });

            if (creationErrors.length > 0) {
                setErrors(creationErrors);
            }

            return templateConfig;
        } catch (error) {
            setErrors([`Failed to create template sections: ${error.message}. Please check your data and try again, or refresh the page to start over.`]);
            return existingTemplate;
        }
    };

    return {
        importedData,
        errors,
        exportTemplateFormFields,
        importTemplateFormFields,
        createTemplateSections,
        setErrors
    };
};

export default useTemplateFormFieldsHandler;
